<template>
  <div class="card mb-2">
    <div class="card-header bg-primary text-white d-inline-flex">
      <a v-b-toggle="`collapse-view-instrument-card`" class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">Instrument Details</span>

      <span class="text-white ml-auto c-pointer">
        <span
          v-if="$store.getters['User/isCurrentUserAdmin'] && canViewDeleteButton()"
          @click="deleteConfirmationModal"
        >
          Delete |
        </span>
        <span
          v-if="!$store.getters['User/isCurrentUserViewOnly']"
          @click="editInstrumentDetails"
        >
          Edit
        </span>
      </span>
    </div>
    <b-collapse :id="`collapse-view-instrument-card`" v-model="showCollapse">
      <div class="card-body">
        <LoadingSpinner v-if="instrumentsLoading" message ='Loading Instrument Details' size = '1x'>
        </LoadingSpinner>
        <template v-if="!instrumentsLoading">
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Agreement Type
            </div>
            <div class="col-sm-8">
              <template v-if="agreementTypesLoading">
                Loading...
              </template>
              <template v-if="!agreementTypesLoading">
                {{getAgreementTypeName(currentInstrument.agreementTypeId)}}
              </template>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument ID
            </div>
            <div class="col-sm-8">
              {{ currentInstrument.id }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument Type
            </div>
            <div class="col-sm-8">
              <template v-if="typesLoading">
                Loading...
              </template>
              <template v-if="!typesLoading">
                {{ getTypeString(currentInstrument.typeId) }}
              </template>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument Status
            </div>
            <div class="col-sm-8">
              <template v-if="instrumentStatusLoading">
                Loading...
              </template>
              <template v-if="!instrumentStatusLoading">
                <font-awesome-icon
                  :id="'currentinst-' + currentInstrument.id"
                  icon="circle"
                  class="rounded-circle border border-white border-circle"
                  size="sm"
                  :class="getStatusClass(currentInstrument.statusId, 'Instrument')" />
                {{getStatusName(currentInstrument.statusId) || 'None'}}
              </template>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument Number
            </div>
            <div class="col-sm-8">
              {{currentInstrument.instrumentNumber || 'None'}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Locality
            </div>
            <div class="col-sm-8">
              <template v-if="localityLoading">
                Loading...
              </template>
              <template v-if="!localityLoading">
                {{getLocalityName(currentInstrument.localityId) || 'None'}}
              </template>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Deed Book/Page
            </div>
            <div class="col-sm-8">
              {{currentInstrument.deedBookPage || 'None'}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Staff Lead
            </div>
            <div class="col-sm-8">
              {{ currentInstrument.staffLeadName }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument Tags
            </div>
            <div class="col-sm-8">
              <div v-if="tagTypeLoading">
                Loading...
              </div>
              <div v-if="!anyTagsExist()">
                None
              </div>
              <template v-if="anyTagsExist()">
                <template v-for="tag in currentTags">
                    <span v-if="tag.id === 1" :key="`it-${tag.id}`" class="badge badge-danger mr-1">
                      {{ tag.name }}
                    </span>
                  <span v-else :key="`it-${tag.id}`" class="badge badge-info mr-1">
                      {{ tag.name }}
                    </span>
                </template>
              </template>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Instrument Notes
            </div>
            <div class="col-sm-8 notes-field">{{currentInstrument.notes || 'None'}}</div>
          </div>
          <div class="form-group row" v-if="currentInstrument.legacyControlNumber">
            <div class="col-sm-4 label-text">
              Legacy Control Number(s)
            </div>
            <div class="col-sm-8">
              {{currentInstrument.legacyControlNumber}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Specific Terms
            </div>
            <div class="col-sm-8">
              <div v-if="specificTermLoading">
                Loading...
              </div>
              <div v-if="!anyTermsExist()">
                None
              </div>
              <ul class="details-list" v-if="anyTermsExist()">
                <li v-for="term in currentTerms"
                    :key="`st-${term.id}`">
                  {{ term.name }}
                </li>
                <li v-for="ut in orderedUniqueTerms"
                    :key="`ut-${ut.id}`">
                  Unique Term - {{ ut.description }}
                </li>
              </ul>
            </div>
          </div>
          <div class="d-flex justify-content-end">
            <DownloadFile v-if="!instrumentsLoading"
              :entityId="currentInstrument.id"
              :urlToDownloadFrom="this.urlToDownloadFrom"
              :buttonText="'Instrument Summary Output'"
              :contentType="contentType"
              :defaultFileName="'InstrumentSummaryOutput.xlsx'"
            />
          </div>
        </template>
      </div>
    </b-collapse>
    <b-modal ref="deleteConfirmation" :id="`${currentInstrument.id}`" :title="`Delete Instrument ${currentInstrument.id}`">
      Are you sure you want to delete this Instrument? All associated Conservation Purposes,
      Specific Terms – Building/Activity Areas, Specific Terms – Water Quality Protection Areas, Associated
      Partnership Grants, Reviews, Contacts, and Events will be deleted.
      <div slot="modal-footer">
        <b-button variant="danger" class="button-row-spacing" @click="hideModal">
          Cancel
        </b-button>
        <b-button @click="onDeleteConfirmed()">
          Delete
        </b-button>
      </div>
    </b-modal>
    <b-modal ref="deleteBlockerModal" :id="`blocker-${currentInstrument.id}`" :title="`Cannot Delete Instrument ${currentInstrument.id}`">
      {{ deleteBlockMessage }}
      <div slot="modal-footer">
        <b-button @click="hideDeleteBlockerModal">
          Okay
        </b-button>
      </div>
    </b-modal>
    <b-modal ref="deleteSuccessModal" :id="`deleteSuccess-${currentInstrument.id}`" :title="`Successfully Deleted Instrument ${currentInstrument.id}`">
      Folders in Box must be deleted manually.
      <div slot="modal-footer">
        <b-button @click="hideDeleteSuccessModal">
          Okay
        </b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import LoadingSpinner from '../LoadingSpinner.vue';
import CollapseControl from '../../mixins/CollapseControl';
import StatusHelper from '../../mixins/StatusHelper';
import DownloadFile from '../common/DownloadFile.vue';


export default {
  name: 'InstrumentDetailsCard',
  mixins: [CollapseControl, StatusHelper],
  data() {
    return {
      contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      showCollapse: true,
      deleteBlockMessage: null,
    };
  },
  components: {
    LoadingSpinner,
    DownloadFile,
  },
  computed: {
    ...mapState(LookupModules.LOCALITIES.namespace, {
      localityLoading: state => state.loading,
    }),
    ...mapState({
      currentInstrument: state => state.Instrument.currentInstrument,
      instrumentsLoading: state => state.Instrument.instrumentsLoading,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_STATUSES.namespace, {
      instrumentStatusLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_TYPES.namespace, {
      typesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SPECIFIC_TERMS.namespace, {
      specificTermLoading: state => state.loading,
    }),
    ...mapState(LookupModules.TAG_TYPES.namespace, {
      tagTypeLoading: state => state.loading,
    }),
    ...mapGetters('Instrument', ['instrumentCount']),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      getLocalityName: 'getName',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      getAgreementTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_TYPES.namespace, {
      getTypeName: 'getName',
      getTypeProperty: 'getProperty',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_STATUSES.namespace, {
      getStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.SPECIFIC_TERMS.namespace, {
      getSpecificTermName: 'getName',
      specificTermOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.TAG_TYPES.namespace, { 
      getTagTypeName: 'getName',
      tagTypeOptions: 'getActiveOptions',
    }),
    urlToDownloadFrom() {
      // return `/instrument/${this.currentInstrument.id}/ExportEasementSummaryReport/${this.$route.params.project_id}`;
      return `/instrument/${this.currentInstrument.id}/ExportExcelEasmentReport`;
    },
    orderedUniqueTerms() {
      return this.currentInstrument.uniqueTerms.slice().sort(this.uniqueTermsSort);
    },
    currentTerms: function currentTerms() {
      return this.specificTermOptions
        .filter(st => this.currentInstrument.specificTermIds.includes(st.id));
    },
    currentTags: function currentTags() {
      return this.tagTypeOptions
        .filter(st => this.currentInstrument.tagTypeIds.includes(st.id));
    },
    associatedProperties() {
      return this.$store.state[EntityModules.ASSOCIATED_PROPERTIES.namespace].entities;
    },
  },
  created() {
    // fetch lookup data
    this.$store.dispatch(`${LookupModules.AGREEMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.AGREEMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_ROLES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_ROLES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_STATUSES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_STATUSES.url);
    this.$store.dispatch(`${LookupModules.LOCALITIES.namespace}/fetchOptions`,
      LookupModules.LOCALITIES.url);
    this.$store.dispatch(`${LookupModules.SPECIFIC_TERMS.namespace}/fetchOptions`,
      LookupModules.SPECIFIC_TERMS.url);
    this.$store.dispatch(`${LookupModules.TAG_TYPES.namespace}/fetchOptions`,
      LookupModules.TAG_TYPES.url);
  },
  methods: {
    editInstrumentDetails() {
      this.$store.dispatch('Instrument/switchToEdit');
      this.$store.dispatch('Common/startEditMode', 'Instrument');
    },
    canViewDeleteButton() {
      if (this.currentInstrument.statusId !== 3) return false;
      if (this.instrumentCount <= 1) return false;
      return true;
    },
    deleteConfirmationModal() {
      // if project.instruments <= 1 --> can not delete
      if (this.instrumentCount <= 1) {
        this.deleteBlockMessage = 'Project must have at least one instrument.  Can not delete the last instrument in the project';
        this.$refs.deleteBlockerModal.show();
        return;
      }
      // if currentInstrument !== inactive --> can not delete
      if (this.currentInstrument.statusId !== 3) {
        this.deleteBlockMessage = 'Instrument status must be set to inactive before it is deleted.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      // if currentInstrument.associatedProperties > 1 -->can not delete
      if (this.associatedProperties && this.associatedProperties.length !== 1) {
        this.deleteBlockMessage = 'You must remove the Associated Property. Only one (1) Property can be associated to an Instrument to be deleted.';
        this.$refs.deleteBlockerModal.show();
        return;
      }

      // if currentInstrument.assocatedProperties === 1 AND property values are not all 0 -->can not delete
      if (this.associatedProperties && this.associatedProperties.length === 1) {
        const assProp = this.associatedProperties[0].propertyInstruments[0];

        const assPropDeleteMessage = 'You must zero out or set to "N/A" all Property Acres and Development Rights values before the Instrument can be deleted.';

        if (!(parseInt(assProp.dwellingsAllowed, 10) === 0 || assProp.dwellingsAllowed === null)) {
          this.deleteBlockMessage = assPropDeleteMessage;
          this.$refs.deleteBlockerModal.show();
          return;
        }

        if (!(parseInt(assProp.finishedAreaAllowed, 10) === 0 || assProp.finishedAreaAllowed === null)) {
          this.deleteBlockMessage = assPropDeleteMessage;
          this.$refs.deleteBlockerModal.show();
          return;
        }
        if (!(parseInt(assProp.futurePropertiesAllowed, 10) === 0 || assProp.futurePropertiesAllowed === null)) {
          this.deleteBlockMessage = assPropDeleteMessage;
          this.$refs.deleteBlockerModal.show();
          return;
        }
        if (!(parseInt(assProp.imperviousAreaAllowed, 10) === 0 || assProp.imperviousAreaAllowed === null)) {
          this.deleteBlockMessage = assPropDeleteMessage;
          this.$refs.deleteBlockerModal.show();
          return;
        }
        if (!(parseInt(assProp.propertyAcres, 10) === 0 || assProp.propertyAcres === null)) {
          this.deleteBlockMessage = assPropDeleteMessage;
          this.$refs.deleteBlockerModal.show();
          return;
        }
      }

      this.$refs.deleteConfirmation.show();
    },
    onDeleteConfirmed() {
      this.$store.dispatch('Instrument/deleteInstrument', { instrumentId: this.currentInstrument.id, submittedCallback: this.showDeleteSuccessModal });
    },
    anyTermsExist() {
      return this.currentInstrument
        && ((this.currentInstrument.specificTermIds
          && this.currentInstrument.specificTermIds.length > 0)
          || (this.currentInstrument.uniqueTerms
            && this.currentInstrument.uniqueTerms.length > 0));
    },
    anyTagsExist() {
      return this.currentInstrument
        && (this.currentInstrument.tagTypeIds
          && this.currentInstrument.tagTypeIds.length > 0);
    },
    uniqueTermsSort(a, b) {
      return a.id - b.id;
    },
    getTypeString(id) {
      return id ? `${this.getTypeName(id)} (${this.getTypeProperty(id, 'code')})` : 'None';
    },
    deleteInstrument() {

    },
    hideModal() {
      this.$refs.deleteConfirmation.hide();
    },
    hideDeleteBlockerModal() {
      this.deleteBlockMessage = null;
      this.$refs.deleteBlockerModal.hide();
    },
    showDeleteSuccessModal() {
      this.hideModal();
      this.$refs.deleteSuccessModal.show();
    },
    hideDeleteSuccessModal() {
      this.$refs.deleteSuccessModal.hide();
      this.$store.dispatch('Instrument/forceFetchInstruments', this.$store.state.CurrentProject.currentProject.id)
        .then(() => {
          this.$store.dispatch('Service/forceFetchServicesWithNoLoadingIndicator', parseInt(this.$route.params.project_id, 10));
        })
        .then(() => {
          this.$router.push({
            name: 'projectInstruments',
            params: { project_id: parseInt(this.$route.params.project_id, 10) },
          });
        });
    },
  },
};
</script>
